import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import Navbar from "../components/Navbar";
import CaseStatusChip from "../components/SupportCases/CaseStatusChip";
import {
  apiGetSupportCase,
  apiAddSupportCaseMessage,
  apiAddSupportCaseEvidence,
  apiEscalateSupportCase,
  apiReopenSupportCase,
  apiRateSupportCase,
  supportCaseErrorMessage,
} from "../api/supportCaseApi";
import {
  readCase,
  forCustomerDisplay,
  caseCategoryLabel,
  caseStatusLabel,
  caseResolutionLabel,
  caseAuthorLabel,
  describeCaseSla,
  parseEvidenceUrls,
  canRateCase,
  canReopenCase,
  canMessageCase,
  canEscalateCase,
} from "../utils/supportCaseVocabulary";

/**
 * SupportCaseDetailPage: one reported issue, start to finish.
 *
 * Everything a customer is allowed to see about a case, in the order they need
 * it: where it stands and when to expect us, then the conversation, then what
 * they sent us, then how it got here, then the answer, then the one question we
 * get to ask them back.
 *
 * Every field rendered here comes out of forCustomerDisplay, which keeps
 * internal notes, who a case was assigned to, who escalated it, its tags and
 * the money on its resolution out of this component entirely. The server
 * redacts all of that for a customer; not reading it here as well is what makes
 * a server slip harmless rather than a leak.
 */
const Stars = ({ value = 0, onChange }) => (
  <StarRow>
    {[1, 2, 3, 4, 5].map((star) => {
      const Icon = star <= Math.round(value) ? StarIcon : StarBorderIcon;
      return (
        <StarBtn
          key={star}
          type="button"
          aria-label={`${star} out of 5`}
          onClick={() => onChange?.(star)}
          $readonly={typeof onChange !== "function"}
        >
          <Icon fontSize="medium" />
        </StarBtn>
      );
    })}
  </StarRow>
);

const SupportCaseDetailPage = () => {
  const { caseId } = useParams();
  const history = useHistory();

  const [supportCase, setSupportCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [messageBody, setMessageBody] = useState("");
  const [evidenceText, setEvidenceText] = useState("");
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    apiGetSupportCase(caseId)
      .then((data) => {
        if (!alive) return;
        const next = forCustomerDisplay(readCase(data));
        if (next) setSupportCase(next);
        else setError("We could not find that case.");
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(supportCaseErrorMessage(err, "We could not load that case."));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [caseId]);

  /* Every action ends the same way: take the case back off the response when it
     is there, otherwise refetch. One helper so no action can forget. */
  const applyResult = (data) => {
    const next = forCustomerDisplay(readCase(data));
    if (next) {
      setSupportCase(next);
      return Promise.resolve();
    }
    return apiGetSupportCase(caseId).then((fresh) => {
      const refreshed = forCustomerDisplay(readCase(fresh));
      if (refreshed) setSupportCase(refreshed);
    });
  };

  const run = async (work, failure) => {
    setBusy(true);
    try {
      const data = await work();
      await applyResult(data);
      return true;
    } catch (err) {
      toast.error(supportCaseErrorMessage(err, failure));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const sendMessage = async () => {
    const body = messageBody.trim();
    if (!body) {
      toast.warn("Write something first.");
      return;
    }
    const ok = await run(
      () => apiAddSupportCaseMessage(caseId, { body }),
      "We could not add your message."
    );
    if (ok) {
      setMessageBody("");
      toast.success("Message added.");
    }
  };

  const addEvidence = async () => {
    const { evidence, rejected } = parseEvidenceUrls(evidenceText);
    if (rejected.length) {
      toast.error("Each line has to be a full link starting with http or https.");
      return;
    }
    if (!evidence.length) {
      toast.warn("Add a link first.");
      return;
    }
    const ok = await run(
      () => apiAddSupportCaseEvidence(caseId, evidence),
      "We could not attach that."
    );
    if (ok) {
      setEvidenceText("");
      toast.success("Attached.");
    }
  };

  const escalate = async () => {
    const reason = window.prompt("Tell us in one line why this needs more attention:");
    if (reason === null) return;
    const ok = await run(
      () => apiEscalateSupportCase(caseId, reason.trim()),
      "We could not escalate this case."
    );
    if (ok) toast.success("Escalated. Someone senior will pick this up.");
  };

  const reopen = async () => {
    const reason = window.prompt("What is still wrong?");
    if (reason === null) return;
    const ok = await run(
      () => apiReopenSupportCase(caseId, reason.trim()),
      "We could not reopen this case."
    );
    if (ok) toast.success("Reopened.");
  };

  const rate = async () => {
    if (!rating) {
      toast.warn("Pick a rating first.");
      return;
    }
    const ok = await run(
      () => apiRateSupportCase(caseId, { rating, comment: ratingComment.trim() }),
      "We could not save your rating."
    );
    if (ok) {
      setRatingComment("");
      toast.success("Thank you, that helps.");
    }
  };

  const sla = supportCase ? describeCaseSla(supportCase) : null;
  const stamp = (value) => (value ? moment(value).format("lll") : "");

  return (
    <>
      <Navbar />
      <Page>
        <Back type="button" onClick={() => history.push("/support-cases")}>
          Back to my reported issues
        </Back>

        {loading && <Muted>Loading this case...</Muted>}
        {!loading && error && <Muted>{error}</Muted>}

        {!loading && !error && supportCase && (
          <>
            <Header>
              <HeaderTop>
                <CaseRef>{supportCase.caseNumber}</CaseRef>
                <CaseStatusChip status={supportCase.status} />
              </HeaderTop>
              <h1>{supportCase.subject}</h1>
              <HeaderMeta>
                {caseCategoryLabel(supportCase.category)}
                {supportCase.order?.number ? ` on order ${supportCase.order.number}` : ""}
                {supportCase.vendor?.name ? ` with ${supportCase.vendor.name}` : ""}
              </HeaderMeta>
              <HeaderMeta>Reported {stamp(supportCase.createdAt)}</HeaderMeta>
              {sla && (
                <SlaLine $tone={sla.tone}>
                  {sla.label}
                  {sla.dueAt ? `, target ${stamp(sla.dueAt)}` : ""}
                </SlaLine>
              )}
              {supportCase.escalationLevel > 0 && (
                <HeaderMeta>This case has been escalated for extra attention.</HeaderMeta>
              )}
              <Actions>
                {canEscalateCase(supportCase) && (
                  <Ghost type="button" disabled={busy} onClick={escalate}>
                    Ask for more attention
                  </Ghost>
                )}
                {canReopenCase(supportCase) && (
                  <Ghost type="button" disabled={busy} onClick={reopen}>
                    Reopen this case
                  </Ghost>
                )}
              </Actions>
            </Header>

            <Card>
              <CardTitle>What you told us</CardTitle>
              <Body>{supportCase.description}</Body>
            </Card>

            {supportCase.resolution && (
              <Card $accent="#bbf7d0">
                <CardTitle>How it was resolved</CardTitle>
                {supportCase.resolution.outcome && (
                  <Outcome>{caseResolutionLabel(supportCase.resolution.outcome)}</Outcome>
                )}
                {supportCase.resolution.summary && <Body>{supportCase.resolution.summary}</Body>}
                {supportCase.resolution.resolvedAt && (
                  <Stamp>Resolved {stamp(supportCase.resolution.resolvedAt)}</Stamp>
                )}
              </Card>
            )}

            <Card>
              <CardTitle>Messages</CardTitle>
              {supportCase.messages.length === 0 && (
                <Muted>No replies yet. We will write here as soon as we have news.</Muted>
              )}
              <Thread>
                {supportCase.messages.map((message, index) => {
                  const mine = message.authorRole === "CUSTOMER";
                  return (
                    <Bubble key={`${message.createdAt || index}-${index}`} $mine={mine}>
                      <Who>
                        {mine ? "You" : message.authorName || caseAuthorLabel(message.authorRole)}
                        <Stamp as="span"> {stamp(message.createdAt)}</Stamp>
                      </Who>
                      <Body>{message.body}</Body>
                      {message.attachments.length > 0 && (
                        <Attachments>
                          {message.attachments.map((file, i) => (
                            <a
                              key={`${file.url}-${i}`}
                              href={file.url}
                              target="_blank"
                              rel="noreferrer noopener"
                            >
                              {file.name || "Attachment"}
                            </a>
                          ))}
                        </Attachments>
                      )}
                    </Bubble>
                  );
                })}
              </Thread>

              {canMessageCase(supportCase) ? (
                <Composer>
                  <TextArea
                    rows={4}
                    value={messageBody}
                    placeholder="Add anything else we should know"
                    aria-label="Add a message"
                    onChange={(e) => setMessageBody(e.target.value)}
                  />
                  <Primary type="button" disabled={busy} onClick={sendMessage}>
                    {busy ? "Sending..." : "Send message"}
                  </Primary>
                </Composer>
              ) : (
                <Muted>
                  This case is {caseStatusLabel(supportCase.status).toLowerCase()}, so the
                  conversation is closed. Reopen it if something is still wrong.
                </Muted>
              )}
            </Card>

            <Card>
              <CardTitle>What you sent us</CardTitle>
              {supportCase.evidence.length === 0 && <Muted>Nothing attached yet.</Muted>}
              <EvidenceList>
                {supportCase.evidence.map((item, index) => (
                  <li key={`${item.url}-${index}`}>
                    <a href={item.url} target="_blank" rel="noreferrer noopener">
                      {item.caption || item.url}
                    </a>
                    <Stamp as="span">
                      {item.type ? ` ${item.type}` : ""}
                      {item.uploadedAt ? `, added ${stamp(item.uploadedAt)}` : ""}
                    </Stamp>
                  </li>
                ))}
              </EvidenceList>
              {canMessageCase(supportCase) && (
                <Composer>
                  <TextArea
                    rows={2}
                    value={evidenceText}
                    placeholder="One link per line, for example https://example.com/photo.jpg"
                    aria-label="Add links to photos or files"
                    onChange={(e) => setEvidenceText(e.target.value)}
                  />
                  <Ghost type="button" disabled={busy} onClick={addEvidence}>
                    Attach links
                  </Ghost>
                </Composer>
              )}
            </Card>

            <Card>
              <CardTitle>History</CardTitle>
              {supportCase.statusHistory.length === 0 && (
                <Muted>Nothing has changed since you reported it.</Muted>
              )}
              <History>
                {supportCase.statusHistory.map((entry, index) => (
                  <li key={`${entry.at || index}-${index}`}>
                    <HistoryWhen>{stamp(entry.at)}</HistoryWhen>
                    <div>
                      {entry.from
                        ? `${caseStatusLabel(entry.from)} to ${caseStatusLabel(entry.to)}`
                        : caseStatusLabel(entry.to)}
                      {entry.note ? `: ${entry.note}` : ""}
                    </div>
                  </li>
                ))}
              </History>
            </Card>

            {supportCase.satisfaction && (
              <Card>
                <CardTitle>Your rating</CardTitle>
                <Stars value={supportCase.satisfaction.rating} />
                {supportCase.satisfaction.comment && <Body>{supportCase.satisfaction.comment}</Body>}
              </Card>
            )}

            {canRateCase(supportCase) && (
              <Card $accent="#fde68a">
                <CardTitle>How did we do</CardTitle>
                <Body>
                  This case is {caseStatusLabel(supportCase.status).toLowerCase()}. Rating it
                  takes a second and it is the only way we find out where support is falling
                  short.
                </Body>
                <Stars value={rating} onChange={setRating} />
                <TextArea
                  rows={3}
                  value={ratingComment}
                  placeholder="Anything you want to add (optional)"
                  aria-label="Comment on your rating"
                  onChange={(e) => setRatingComment(e.target.value)}
                />
                <Primary type="button" disabled={busy || !rating} onClick={rate}>
                  {busy ? "Saving..." : "Send rating"}
                </Primary>
              </Card>
            )}
          </>
        )}
      </Page>
    </>
  );
};

const Page = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: calc(var(--lusuku-nav-h, 130px) + 12px) 16px 56px;
  min-height: 60vh;
`;

const Back = styled.button`
  border: none;
  background: transparent;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 0 12px;
`;

const Muted = styled.div`
  color: #6b7280;
  font-size: 13.5px;
  padding: 10px 0;
  line-height: 1.55;
`;

const Header = styled.div`
  background: #ffffff;
  border: 1px solid #eef0f2;
  border-radius: 14px;
  padding: 18px 20px;
  margin-bottom: 12px;

  h1 {
    font-size: 21px;
    font-weight: 800;
    color: #1f2937;
    margin: 6px 0 8px;
    word-break: break-word;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const CaseRef = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: #9ca3af;
`;

const HeaderMeta = styled.div`
  font-size: 12.5px;
  color: #6b7280;
  margin-top: 3px;
`;

const SlaLine = styled.div`
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$tone === "warn" ? "#b45309" : p.$tone === "good" ? "#15803d" : "#1d4ed8")};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid ${(p) => p.$accent || "#eef0f2"};
  border-radius: 14px;
  padding: 18px 20px;
  margin-bottom: 12px;
`;

const CardTitle = styled.h2`
  font-size: 14px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 10px;
`;

const Body = styled.div`
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Outcome = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #15803d;
  margin-bottom: 6px;
`;

const Stamp = styled.div`
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 4px;
  font-weight: 500;
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
`;

const Bubble = styled.div`
  border-radius: 12px;
  padding: 11px 14px;
  max-width: 88%;
  align-self: ${(p) => (p.$mine ? "flex-end" : "flex-start")};
  background: ${(p) => (p.$mine ? "#f2f7ea" : "#f6f7f8")};
  border: 1px solid ${(p) => (p.$mine ? "#dbe9c8" : "#eceef0")};
`;

const Who = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #4b5563;
  margin-bottom: 4px;
`;

const Attachments = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 7px;

  a {
    font-size: 12.5px;
    color: #1d4ed8;
  }
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 9px;
  margin-top: 12px;
`;

const TextArea = styled.textarea`
  width: 100%;
  border-radius: 9px;
  border: 1px solid #d8dbde;
  padding: 10px 12px;
  font-size: 14px;
  color: #1f2937;
  font-family: inherit;
  resize: vertical;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: #5d8f24;
  }
`;

const Primary = styled.button`
  border: none;
  border-radius: 9px;
  padding: 10px 18px;
  cursor: pointer;
  background: #5d8f24;
  color: #ffffff;
  font-size: 13.5px;
  font-weight: 700;

  &:disabled {
    background: #b6c8a3;
    cursor: default;
  }
`;

const Ghost = styled.button`
  border: 1px solid #d8dbde;
  border-radius: 9px;
  padding: 10px 16px;
  cursor: pointer;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;

  &:disabled {
    color: #9ca3af;
    cursor: default;
  }
`;

const EvidenceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  a {
    font-size: 13.5px;
    color: #1d4ed8;
    word-break: break-all;
  }
`;

const History = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;

  li {
    display: flex;
    gap: 12px;
    font-size: 13px;
    color: #374151;
    line-height: 1.5;
  }
`;

const HistoryWhen = styled.div`
  flex: none;
  width: 150px;
  font-size: 12px;
  color: #9ca3af;
`;

const StarRow = styled.div`
  display: flex;
  gap: 2px;
  margin: 6px 0 10px;
`;

const StarBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: #f59e0b;
  cursor: ${(p) => (p.$readonly ? "default" : "pointer")};
  line-height: 1;
`;

export default SupportCaseDetailPage;
